import { Outlet, useParams, useLocation } from "react-router-dom";
import AppLayout from "@/layouts/AppLayout";
import Breadcrumb from "@/components/Breadcrumb";
import ObjectHeading from "@/components/ObjectHeading";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { Path, useNavigate } from "@/router";
import { useReturnTo } from "@/hooks/useSearchParams";
import PageTitle from "@/components/PageTitle";
import { useQuery } from "@apollo/client";
import { GET_ACTIVITY_TYPE } from "../queries";
import { renderCreditRate } from "../_utils";
import MobileActivityTypeLandingPage from "./MobileActivityTypeLandingPage";
import { getActivityTypeNavLinks } from "./_utils";

export interface ActivityTypeOutletContext {
  activityData: any;
}

export default function ActivityTypePage() {
  const { activityTypeId = "" } = useParams();
  const returnTo = useReturnTo();
  const location = useLocation();
  const navigate = useNavigate();
  const { isCompactLayout: isMobile } = useIsCompactLayout();
  const { data: activityTypeData } = useQuery<any>(GET_ACTIVITY_TYPE, {
    variables: {
      id: activityTypeId
    }
  });

  // Check if we're on a participation detail page, activity instance page, or create page - if so, render without layout
  const isParticipationDetailPage = /\/participants\/[^/]+$/.test(location.pathname);
  const isActivityInstancePage = /\/activity\/[^/]+/.test(location.pathname);
  const isCreatePage = location.pathname.includes("/create");

  if (isParticipationDetailPage || isActivityInstancePage || isCreatePage) {
    return <Outlet />;
  }

  // Show mobile landing page when on index route (exactly /activities/activity-types/:id)
  const normalizedPath = location.pathname.replace(/\/$/, "");
  const isIndexRoute = normalizedPath === `/activities/activity-types/${activityTypeId}`;
  if (isMobile && isIndexRoute) {
    return <MobileActivityTypeLandingPage />;
  }

  const activityData = activityTypeData?.activityType;
  const navLinks = getActivityTypeNavLinks(activityTypeId, activityData?.title);

  const segmentTitles: Record<string, string> = {
    participants: "Participants",
    activities: "Activities"
  };
  const lastSegment = location.pathname.replace(/\/$/, "").split("/").pop() || "";
  const compactTitle = segmentTitles[lastSegment] || "Activity Type Details";

  const renderDesktopLayout = () => (
    <>
      <PageTitle pattern={location.pathname} title={`Activities | ${activityData?.title || "Activity Type"}`} />
      {activityData && (
        <ObjectHeading
          title={activityData.title}
          metaData={[
            ...(activityData?.config
              ? [
                  {
                    text: renderCreditRate(activityData?.config?.creditRates),
                    label: "PDR Credit Rate"
                  }
                ]
              : []),
            { text: activityData.verification, label: "Verification" }
          ]}
          iconName="assignment"
          isDetailsView
        />
      )}
      <div data-testid="activity-type-page">
        <Outlet context={{ activityData } satisfies ActivityTypeOutletContext} />
      </div>
    </>
  );

  const renderMobileLayout = () => (
    <>
      <Breadcrumb
        compactOnly
        compactTitle={compactTitle}
        backLink={{ to: "/activities/activity-types" }}
        onBackClick={() => {
          const backTo = returnTo || `/activities/activity-types/${activityTypeId}`;
          navigate(backTo as Path);
        }}
      />
      {renderDesktopLayout()}
    </>
  );

  return (
    <AppLayout navLinks={navLinks} interior bottomBar>
      {isMobile ? renderMobileLayout() : renderDesktopLayout()}
    </AppLayout>
  );
}
